import { useState, useSyncExternalStore } from "react";
import { perf, type PerfSpan } from "@/lib/perf";

/**
 * Panneau de diagnostic de performance, visible uniquement quand
 * l'interrupteur `?debug=perf` est actif (voir `bootstrapPerf`).
 *
 * Rendu à zéro coût quand le traçage est éteint : `perf.enabled` est faux, on
 * retourne `null` avant tout abonnement. C'est ce qui permet de le monter
 * inconditionnellement dans App.tsx sans peser sur la production.
 */
export function PerfOverlay() {
  // `version()` change à chaque entrée enregistrée : signal de re-render
  // bon marché, sans copier la timeline à chaque frame.
  const version = useSyncExternalStore(perf.subscribe, perf.version, () => 0);
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  if (!perf.enabled) return null;

  const report = perf.report();
  const spans = report.entries.filter((e): e is PerfSpan => e.kind === "span");
  const { summary, vitals } = report;
  const scale =
    summary.spanWallMs && summary.spanWallMs > 0 ? summary.spanWallMs : 1;
  const origin = summary.firstSpanStartMs ?? 0;
  const serial = new Set(summary.serialChain);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(perf.toJSON());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Presse-papier refusé (contexte non sécurisé, permission) : on laisse
      // l'utilisateur récupérer le rapport via window.__encrelioPerf.toJSON().
      setCopied(false);
    }
  };

  return (
    <aside
      aria-label="Diagnostic de performance"
      data-perf-version={version}
      className="fixed bottom-3 right-3 z-[9999] flex max-h-[70vh] w-[420px] max-w-[calc(100vw-24px)] flex-col overflow-hidden text-caption rounded-card"
      style={{
        background: "#0f1b3c",
        color: "#e6ebf7",
        boxShadow: "0 10px 30px rgb(0 0 0 / 0.35)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      }}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-h-6 cursor-pointer items-center gap-1.5"
          style={{ color: "#e6ebf7" }}
        >
          <span aria-hidden>{open ? "▾" : "▸"}</span>
          <strong>perf</strong>
        </button>
        <span className="opacity-65">
          {summary.spanCount} spans
          {summary.droppedEntries > 0
            ? ` · ${summary.droppedEntries} perdus`
            : ""}
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={copy}
            title="Copier le rapport JSON (UUID et emails masqués)"
            className="min-h-6 cursor-pointer px-1.5 rounded-[5px]"
            style={{
              color: "#e6ebf7",
              border: "1px solid rgb(255 255 255 / 0.25)",
            }}
          >
            {copied ? "copié" : "copier"}
          </button>
          <button
            type="button"
            onClick={() => perf.disable()}
            title="Couper le traçage et oublier l'interrupteur"
            aria-label="Couper le traçage"
            className="min-h-6 w-6 cursor-pointer rounded-[5px]"
            style={{
              color: "#e6ebf7",
              border: "1px solid rgb(255 255 255 / 0.25)",
            }}
          >
            ×
          </button>
        </span>
      </div>

      {open && (
        <div className="flex flex-col gap-2 overflow-y-auto px-3 pb-3">
          <Row label="TTFB" value={ms(vitals.ttfbMs)} />
          <Row label="FCP" value={ms(vitals.fcpMs)} />
          <Row label="LCP" value={ms(vitals.lcpMs)} />
          <Row
            label="Tâches longues"
            value={
              vitals.longTaskCount === 0
                ? "0"
                : `${vitals.longTaskCount} · ${vitals.longTaskTotalMs} ms`
            }
          />
          <Row
            label="1ʳᵉ donnée"
            value={ms(summary.timeToFirstDataMs)}
            accent
          />
          <Row
            label="Chaîne sérielle"
            value={
              summary.serialChain.length === 0
                ? "—"
                : `${summary.serialChain.length} hops · ${Math.round(summary.serialChainMs)} ms`
            }
            accent
          />
          <Row
            label="Spans parallèles"
            value={String(summary.parallelSpanCount)}
          />

          {vitals.unsupported.length > 0 && (
            <p className="opacity-55">
              Non mesuré par ce navigateur : {vitals.unsupported.join(", ")}
            </p>
          )}

          <div
            className="mt-1 flex flex-col gap-1 pt-2"
            style={{ borderTop: "1px solid rgb(255 255 255 / 0.15)" }}
          >
            {spans.length === 0 && (
              <span className="opacity-55">
                Aucune requête mesurée pour l'instant.
              </span>
            )}
            {spans.map((s, i) => {
              const left = ((s.startMs - origin) / scale) * 100;
              const width = Math.max((s.durationMs / scale) * 100, 0.6);
              const isSerial = serial.has(s.name);
              return (
                <div key={`${s.name}-${i}`} className="flex flex-col gap-0.5">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="flex-1 truncate"
                      title={s.name}
                      style={{ opacity: isSerial ? 1 : 0.75 }}
                    >
                      {isSerial ? "⇢ " : ""}
                      {s.name}
                    </span>
                    {typeof s.meta.rows === "number" && (
                      <span className="opacity-55">{s.meta.rows} l.</span>
                    )}
                    <span className="font-semibold">
                      {Math.round(s.durationMs)} ms
                    </span>
                  </div>
                  <div
                    className="h-1"
                    style={{ background: "rgb(255 255 255 / 0.08)" }}
                  >
                    <div
                      className="h-1"
                      style={{
                        marginLeft: `${Math.min(Math.max(left, 0), 99)}%`,
                        width: `${Math.min(width, 100)}%`,
                        background:
                          s.meta.status === "error"
                            ? "#f0806c"
                            : isSerial
                              ? "#ffc46b"
                              : "#7fb2ff",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}

function ms(v: number | null): string {
  return v === null ? "—" : `${Math.round(v)} ms`;
}

function Row({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="opacity-65">{label}</span>
      <span
        style={{
          fontWeight: accent ? 700 : 500,
          color: accent ? "#ffc46b" : undefined,
        }}
      >
        {value}
      </span>
    </div>
  );
}
